import { useEffect, useState } from 'react'

import {
  FormActionRow,
  Input,
  InputAutocomplete,
  InputDate,
  InputSelect,
  MoreFilter,
  RadioButton
} from 'components'

import { startOfYear } from 'date-fns'
import { UserStorageService } from 'services'

import { ERRORS_INITIAL_STATE } from 'constants/client.constants'
import { submitOnEnter } from 'utils/submitOnEnter'

import { useTranslation } from 'react-i18next'

import {
  INITIAL_STATE_ERRORS,
  suitableType,
  trackables,
  typesDate,
  typeVisible
} from 'constants/traceability.constants'

import { STATES } from 'constants/location.constants'
import { useDispatch, useSelector } from 'react-redux'
import { all as allCity } from 'store/City/City.selector'
import { userActions } from 'store/User'
import { all as allUser } from 'store/User/User.selector'
import { getUserYearInit } from 'utils/GetUserYearFilter'
import * as S from './styled'

// types for filters

export type FilterType =
  | 'default'
  | 'distribution'
  | 'properties'
  | 'distProperties'

type InputGeneralFiltersProps = {
  filter: any
  errors: typeof INITIAL_STATE_ERRORS
  onSubmit: () => void
  clearFilter: () => void
  handleInputData: (_value, _name) => void
  handleSelect: (_value, _name) => void
  accessGroupsFilter: Record<string, any>[]
  industryGroupsFilter: Record<string, any>[]
  filterType?: FilterType
  toggleMoreFilter(): void
}

export function InputGeneralFilters({
  filter,
  errors,
  onSubmit,
  clearFilter,
  handleInputData,
  handleSelect,
  accessGroupsFilter,
  industryGroupsFilter,
  filterType = 'default',
  toggleMoreFilter
}: InputGeneralFiltersProps) {
  const { t } = useTranslation()
  const [formErrors, _setFormErrors] = useState({ ...ERRORS_INITIAL_STATE })
  const { cities } = useSelector(allCity)
  const { filterOptions } = useSelector(allUser)
  const dispatch = useDispatch()

  const isAdmin = UserStorageService.isAdmin()

  const DefaultFilters = () => (
    <>
      {renderDefaultInput('invoice', t('common:invoice'))}
      {renderDefaultInput('order', t('common:orderNumber'))}
      {renderDefaultInput('cargo', t('common:charge'))}
      {renderDefaultInput('clientOrderNumber', t('tables:clientOrderNumber'))}
      {renderDefaultInput('destinationcnpj', t('common:destinationId'))}
      {renderDefaultInput('mainOrderNumber', t('tables:mainOrderNumber'))}
      {renderDefaultInput(
        'countryFinalDestination',
        t('tables:destinationCountry')
      )}
      {renderDefaultInput('containerNumber', t('tables:containerNumber'))}
      {renderDefaultInput('shippingMarkNumber', t('tables:countermark'))}
      {renderDefaultInput('stockcnpj', t('common:shipperCNPJ'))}
      {renderDefaultInput('barcode', t('common:barCode'))}
      {/* Deixar comentado para o filtro de SKU */}
      {/* {renderDefaultInput('sku', t('common:sku'))} */}
      {/* {renderDefaultInput(
        'boningoutindustrycnpj',
        t('common:boningoutindustrycnpj')
      )} */}
      {renderDefaultInput('slaughtersif', t('common:slaughterSIF'))}
      {/* {renderDefaultInput('boningoutinspectionnum', t('common:boningSIF'))} */}
      {renderDefaultInput('producerdoc', t('common:producerDocument'))}
      {renderDefaultInput('ie', t('common:stateRegistration'))}
      {renderDefaultInput('car', t('common:carNumber'))}
      {renderDefaultInput('propname', t('common:corporateNameOwnership'))}
      {renderDefaultInput('producername', t('common:producerName'))}

      {haveProtocols() && (
        <>
          {renderInputSelect(
            'protocols',
            t('common:protocols'),
            protocolsOptions(),
            { md: 2 }
          )}
          {renderDefaultInput('nrProtocols', t('common:nrProtocols'))}
        </>
      )}
      {isAdmin && (
        <S.GridInput item xs={12} sm={4} md={2}>
          {renderInputSelectWithOptions(
            'trackable',
            t('common:traceability'),
            trackables()
          )}
        </S.GridInput>
      )}
      <S.GridInput item xs={12} sm={4} md={2}>
        {renderInputSelectWithOptions(
          'typeVisible',
          'Tipo visualização',
          typeVisible()
        )}
      </S.GridInput>
    </>
  )

  const distFilters = () => (
    <>
      {renderDefaultInput('invoice', t('common:invoice'))}
      {renderDefaultInput('order', t('common:customerReference'))}
      {renderDefaultInput('cargo', t('common:deliveryNumber'))}
      {isAdmin && (
        <>
          {renderDefaultInput('stockcnpj', t('common:billingEntityId'))}
          {renderDefaultInput('destinationcnpj', t('common:customerId'))}
          {renderDefaultInput(
            'countryFinalDestination',
            t('common:destinationCountry')
          )}
        </>
      )}
      {renderDefaultInput('sku', t('tables:productCode'))}
      {renderDefaultInput(
        'boningoutinspectionnum',
        t('common:zfloatEstablishment')
      )}
      {isAdmin && (
        <>
          {renderDefaultInput('batch', t('common:batch'))}
          {renderDefaultInput('purchaseContract', t('common:purchaseContract'))}
          {renderDefaultInput('purchaseOrder', t('common:purchaseOrder'))}
          {renderDefaultInput('shippingMarkNumber', t('common:shippingMark'))}
        </>
      )}
      {renderDefaultInput('car', t('common:carNumber'))}
      {renderDefaultInput('producerdoc', t('common:producerDocument'))}
      {isAdmin && (
        <>
          {renderDefaultInput('propname', t('common:farmName'))}
          {renderDefaultInput('producername', t('common:producerName'))}
          {haveProtocols() && (
            <>
              {renderInputSelect(
                'protocols',
                t('common:protocols'),
                protocolsOptions(),
                { md: 2 }
              )}
              {renderDefaultInput('nrProtocols', t('common:nrProtocols'))}
            </>
          )}
          <S.GridInput item xs={12} sm={4} md={2}>
            {renderInputSelectWithOptions(
              'trackable',
              t('common:traceability'),
              trackables()
            )}
          </S.GridInput>
        </>
      )}
    </>
  )

  const distPropertyFilters = () => (
    <>
      {renderPropertyFields()}
      {isAdmin &&
        renderInputSelect('analyze', t('common:analyze'), suitableType())}
      {renderLocationSelectors()}
      {renderDefaultInput('invoice', t('common:invoice'), {
        md: 3
      })}
      {renderDefaultInput('sku', t('common:sku'), {
        md: 3
      })}
      {isAdmin && haveProtocols() && renderProtocolFields()}
    </>
  )

  const PropertyFilters = () => (
    <>
      {renderPropertyFields()}
      {renderInputSelect('analyze', t('common:analyze'), suitableType())}
      {renderLocationSelectors()}
      {renderDefaultInput('invoice', t('common:invoice'), {
        md: 3
      })}
      {/* Deixar comentado para o filtro de SKU */}
      {/* {renderDefaultInput('sku', t('common:sku'), {
        md: 3
      })} */}
      {haveProtocols() && renderProtocolFields()}
    </>
  )

  type protocolsOptionsType = {
    title: string
    value: string
  }[]
  const protocolsOptions = (): protocolsOptionsType => {
    const options =
      filterOptions.protocols?.map(i => {
        return { title: i.label, value: i.value }
      }) ?? []
    options.unshift({ title: t('certification:all'), value: 'all' })
    options.push({ title: t('certification:none'), value: 'none' })
    return options
  }

  const haveProtocols = () => filterOptions.protocols?.length > 0

  useEffect(() => {
    dispatch(userActions.fetchFilterOptions())
  }, [dispatch])

  // vvvvvvvvvvvvvvvvvv GENERAL INPUT FILTERS vvvvvvvvvvvvvvvvvv
  const BeginDate = () => (
    <InputDate
      error={Boolean(errors.begin)}
      fullWidth
      helperText={errors.begin}
      id="dtStart"
      minDate={startOfYear(new Date(getUserYearInit(isAdmin), 0))}
      label={t('common:initialDate')}
      name="dtStart"
      value={filter.begin}
      onInput={value => handleInputData(value, 'begin')}
    />
  )
  const FinalDate = () => (
    <InputDate
      error={Boolean(errors.end)}
      fullWidth
      helperText={errors.end}
      id="dtFinal"
      label={t('common:finalDate')}
      name="dtFinal"
      value={filter.end}
      onInput={value => handleInputData(value, 'end')}
    />
  )
  const RadioDateFilter = () => (
    <RadioButton
      error={Boolean(errors.typeDate)}
      helperText={errors.typeDate}
      options={typesDate(filterType, isAdmin)}
      label=""
      onSelected={value => handleSelect(value, 'typeDate')}
      value={filter.typeDate}
    />
  )
  const AccessGroupFilter = () => (
    <InputAutocomplete
      padding={0.7}
      defaultValue={filter.accessGroupsFilter}
      error={Boolean(formErrors.accessGroupsFilter)}
      fullWidth
      helperText={formErrors.accessGroupsFilter}
      label={t('common:accessGroupsFilter')}
      onSelected={value => handleSelect(value, 'accessGroupsFilter')}
      optionLabel="accessGroupName"
      options={accessGroupsFilter}
      allowRemoveAllItems={isAdmin}
      disableRemoveButton={!isAdmin && filter.accessGroupsFilter.length === 1}
      clearFilter={clearFilter}
    />
  )
  const IndustryGroupFilter = () => (
    <InputAutocomplete
      padding={0.7}
      defaultValue={filter.industryGroupsFilter}
      error={Boolean(formErrors.industryGroupsFilter)}
      fullWidth
      helperText={formErrors.industryGroupsFilter}
      label={t('common:industryGroupsFilter')}
      onSelected={value => handleSelect(value, 'industryGroupsFilter')}
      optionLabel="industryGroupName"
      options={industryGroupsFilter}
      allowRemoveAllItems={isAdmin}
      disableRemoveButton={isAdmin && filter.industryGroupsFilter.length === 1}
      clearFilter={clearFilter}
    />
  )

  // Helper unificado para renderizar Input com GridInput
  const renderDefaultInput = (
    fieldName: string,
    label: string,
    gridProps = {}
  ) => (
    <S.GridInput item xs={12} sm={4} md={2} {...gridProps}>
      <Input
        error={Boolean(errors[fieldName])}
        fullWidth
        helperText={errors[fieldName]}
        label={label}
        value={filter[fieldName]}
        onKeyPress={submitOnEnter(onSubmit)}
        onInput={value => handleInputData(value, fieldName)}
      />
    </S.GridInput>
  )

  // Helper para renderizar InputSelect com GridInput
  const renderInputSelect = (
    fieldName: string,
    label: string,
    options: any[],
    gridProps = {},
    selectProps = {}
  ) => (
    <S.GridInput item xs={12} sm={4} md={3} {...gridProps}>
      <InputSelect
        error={Boolean(errors[fieldName])}
        fullWidth
        helperText={errors[fieldName]}
        label={label}
        onSelected={value => handleSelect(value, fieldName)}
        optionLabel="title"
        optionValue="value"
        options={options}
        value={filter[fieldName]}
        {...selectProps}
      />
    </S.GridInput>
  )

  // Helper para renderizar campos básicos de propriedade
  const renderPropertyFields = () => (
    <>
      {renderDefaultInput('fazCode', t('tables:fazCode'), {
        md: 3
      })}
      {renderDefaultInput('propname', t('tables:fazName'), {
        md: 3
      })}
      {renderDefaultInput('producername', t('common:producerName'), {
        md: 3
      })}
      {renderDefaultInput('producerdoc', t('common:producerDocument'), {
        md: 3
      })}
      {renderDefaultInput('car', t('common:carNumber'), {
        md: 3
      })}
    </>
  )

  // Helper para renderizar seletores de localização (estado e cidade)
  const renderLocationSelectors = () => (
    <>
      <S.GridInput item xs={12} sm={4} md={3}>
        <InputSelect
          error={Boolean(errors.state)}
          fullWidth
          helperText={errors.state}
          label={t('common:state')}
          onSelected={({ value }) => handleSelect(value, 'state')}
          optionLabel="title"
          optionValue="value"
          options={STATES}
          value={filter.state}
        />
      </S.GridInput>
      <S.GridInput item xs={12} sm={4} md={3}>
        <InputSelect
          disabled={filter.state === ''}
          error={Boolean(errors.city)}
          fullWidth
          helperText={errors.city}
          label={t('common:city')}
          onSelected={value => handleSelect(value.title, 'city')}
          optionLabel="title"
          options={cities.filter(city => city.state === filter.state)}
          optionValue={'title'}
          value={filter.city}
        />
      </S.GridInput>
    </>
  )

  // Helper para renderizar campos de protocolos
  const renderProtocolFields = () => (
    <>
      <S.GridInput item xs={12} sm={4} md={3}>
        {renderInputSelectWithOptions(
          'protocols',
          t('common:protocols'),
          protocolsOptions()
        )}
      </S.GridInput>
      {renderDefaultInput('nrProtocols', t('common:nrProtocols'), {
        md: 3
      })}
    </>
  )

  // ^^^^^^^^^^^^^^^^^ GENERAL INPUT FILTERS ^^^^^^^^^^^^^^^^^

  // Helper unificado para InputSelect com fieldName, label e options
  const renderInputSelectWithOptions = (
    fieldName: string,
    label: string,
    options: any[]
  ) => (
    <InputSelect
      error={Boolean(errors[fieldName])}
      fullWidth
      helperText={errors[fieldName]}
      label={label}
      onSelected={value => handleSelect(value, fieldName)}
      optionLabel="title"
      optionValue="value"
      options={options}
      value={filter[fieldName]}
    />
  )
  // Create a mapping of filter types to their respective functions
  const TYPES = {
    default: DefaultFilters,
    distribution: distFilters,
    properties: PropertyFilters,
    distProperties: distPropertyFilters
  }

  return (
    <S.Wrapper container spacing={4}>
      <S.GridFilter container alignItems="center">
        <S.GridInput item xs={12} sm={4} md={2}>
          {BeginDate()}
        </S.GridInput>
        <S.GridInput item xs={12} sm={4} md={2}>
          {FinalDate()}
        </S.GridInput>
        <S.GridInput item xs={12} sm={4} md={7}>
          {RadioDateFilter()}
        </S.GridInput>
        <S.GridToggleButton xs={12} sm={4} md={1}>
          <MoreFilter
            border="not"
            toggle={toggleMoreFilter}
            arrowAction="close"
          />
        </S.GridToggleButton>
        {AccessGroupFilter()}
        {IndustryGroupFilter()}
        {TYPES[filterType]()}
      </S.GridFilter>
      <S.GridFilter container>
        <S.GridButtons item xs={12} sm={12} md={12} lg={12}>
          <FormActionRow
            onExit={clearFilter}
            onSubmit={onSubmit}
            type="filter"
          />
        </S.GridButtons>
      </S.GridFilter>
    </S.Wrapper>
  )
}
